import ZonePricetypesCollection from './ZonePricetypesCollection';
import PreselectedPricetypeQuantities from './PreselectedPricetypeQuantities';
import ZoneSelectorCollection from './ZoneSelectorCollection';
import WheelchairQuantitySelect from './WheelchairQuantitySelect';
import { flattenPricetypeQuantities, stringifySpecialRequests } from './TicketSelectorUtils';
import Messenger from '../../tnew/ui/Messenger';


/**
 * @typedef {{pricetypeId: number, quantity: number}[]} PricetypeQuantities
 */

const defaultOptions = {
  /** @type {string} */
  selectedZoneId: null,

  /**
   * Sets the pricetype quantities for the given `selectedZoneId`.
   * Ignored if `selectedZoneId` is missing. Currently used when
   * re-displaying an errored Single or Fixed form submission.
   *
   * @type {{pricetypeId: string, quantity: number}}
   * @example {'4': 1, '6': 3} // 1× pricetype 4, 3× pricetype 6
   */
  restoringPricetypeQuantitiesForSelectedZone: null,

  /** @type {function(Event, TicketSelector)} */
  formSubmitOverride: null,

  /**
   * If set, the TicketSelector will act as though the user
   * has selected the given quantities. The user-displayed `PricetypeSelector`s
   * are then "information only". Used on Flex's Selections page.
   *
   * Note that `restoringPricetypeQuantitiesForSelectedZone` and
   * `preselectedPricetypeQuantities` are never intended to be
   * used together.
   *
   * @type {PricetypeQuantities}
   */
  preselectedPricetypeQuantities: null,
};


class TicketSelector {
  /**
   * @param {string|HTMLFormElement} form
   * @param {object} options_
   */
  constructor(form, options_ = {}) {
    this.form = typeof form === 'string' ?
      document.querySelector(form) : form;

    if (!this.form) {
      return;
    }

    this.bindMethods();

    this.initMessenger();

    const options = Object.assign({}, defaultOptions, options_);

    const {
      selectedZoneId,
      preselectedPricetypeQuantities,
      restoringPricetypeQuantitiesForSelectedZone,
    } = options;

    this.zones = new ZoneSelectorCollection(
      this.form,
      selectedZoneId,
    );
    this.pricetypes = new ZonePricetypesCollection(this.form, this.zones.getSelectedZoneId());
    this.wheelchairSelect = new WheelchairQuantitySelect(this.find('.tn-wheel-chair-quantity'));

    this.zonesPlaceholder = this.find('#instructions-choose-zone');
    this.seatingOptionsContainer = this.find('#tn-fieldset-seating-options');
    this.btnSubmit = this.find('button[type="submit"]');

    this.zones.on('update', this.onZoneChange);
    this.pricetypes.on('change', this.updateUi);

    if (preselectedPricetypeQuantities) {
      this.pricetypeQuantities = new PreselectedPricetypeQuantities(preselectedPricetypeQuantities);
    } else {
      this.pricetypeQuantities = this.pricetypes;
    }

    this.formSubmitHandler = (() => {
      if (options.formSubmitOverride) {
        return (event) => {
          options.formSubmitOverride(event, this);
        };
      }
      return this.onFormSubmit;
    })();

    this.form.addEventListener('submit', this.formSubmitHandler);

    if (
      !preselectedPricetypeQuantities &&
      restoringPricetypeQuantitiesForSelectedZone &&
      selectedZoneId !== null &&
      selectedZoneId === this.zones.getSelectedZoneId()
    ) {
      this.pricetypes.restoreSelectedQuantities(restoringPricetypeQuantitiesForSelectedZone);
    }

    this.updateUi();
  }

  bindMethods() {
    this.onZoneChange = this.onZoneChange.bind(this);
    this.updateUi = this.updateUi.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  initMessenger() {
    const messageContainer = document.createElement('div');

    this.messenger = new Messenger(messageContainer);

    this.form.insertAdjacentElement('afterbegin', messageContainer);
  }

  addErrorMessage(message) {
    this.messenger.addErrorMessage(message);
  }

  clearMessages(message) {
    this.messenger.clearMessages(message);
  }

  /**
   * @param {string} selector
   */
  find(selector) {
    return this.form.querySelector(selector);
  }

  onZoneChange(zoneId) {
    this.pricetypes.setCurrentZoneId(zoneId);
    this.updateUi();
  }

  updateUi() {
    const zoneId = this.zones.getSelectedZoneId();
    const totalQuantity = this.pricetypeQuantities.getTotalQuantitySelected();
    this.wheelchairSelect.updateMaxQuantity(totalQuantity);

    if (zoneId && totalQuantity > 0) {
      this.btnSubmit.removeAttribute('disabled');
    } else {
      this.btnSubmit.setAttribute('disabled', 1);
    }

    if (zoneId === null) {
      if (this.zonesPlaceholder) {
        this.zonesPlaceholder.style.display = '';
      }
      if (this.seatingOptionsContainer) {
        this.seatingOptionsContainer.style.display = 'none';
      }
    } else {
      if (this.zonesPlaceholder) {
        this.zonesPlaceholder.style.display = 'none';
      }
      if (this.seatingOptionsContainer) {
        this.seatingOptionsContainer.style.display = '';
      }
    }
  }

  getRequestData() {
    const allPricetypeQuantities = this.pricetypeQuantities.getAllPricetypeQuantities();
    const totalQuantity = this.pricetypeQuantities.getTotalQuantitySelected();
    const zoneId = this.zones.getSelectedZoneId();
    const specialRequests = this.getSpecialRequests(totalQuantity);
    const quantiyArr = flattenPricetypeQuantities(allPricetypeQuantities);

    return {
      allPricetypeQuantities,
      totalQuantity,
      zoneId,
      quantiyArr,
      specialRequests,
    };
  }

  onFormSubmit() {
    const { totalQuantity, zoneId, quantiyArr, specialRequests } = this.getRequestData();

    this.find('#tn-form-event-price-type-id').setAttribute('value', quantiyArr.join(','));
    this.find('#tn-form-event-quantity').setAttribute('value', totalQuantity);
    this.find('#tn-form-event-zone-id').setAttribute('value', zoneId);

    const specialRequestsInput = this.find('#tn-form-event-special-requests');
    if (specialRequestsInput) {
      const specialRequestsString = stringifySpecialRequests(specialRequests);

      specialRequestsInput.setAttribute('value', specialRequestsString);
    }
  }

  /**
   * @param {number} totalQuantity
   */
  getSpecialRequests(totalQuantity) {
    const checkboxContiguous = this.find('#SpecialRequests_IsContiguousSeatsRequested');
    const checkboxNoStairs = this.find('#SpecialRequests_IsNoStairsRequested');
    const selectAislePreference = this.find('#SeatingOptions_AisleOptions');
    const numWheelchairSeats = this.wheelchairSelect.getSelectedQuantity();
    const requests = {};

    if (checkboxContiguous && checkboxContiguous.checked) {
      requests.ContiguousSeats = totalQuantity;
    } else {
      requests.ContiguousSeats = 1;
    }

    if (checkboxNoStairs && checkboxNoStairs.checked) {
      requests.NoStairs = 'Y';
    }

    if (selectAislePreference && selectAislePreference.value) {
      requests.AisleSeat = selectAislePreference.value;
    }

    if (numWheelchairSeats > 0) {
      requests.WheelChairSeats = numWheelchairSeats;
    }

    return requests;
  }

  destroy() {
    this.zones.destroy();
    this.pricetypes.destroy();
    this.pricetypeQuantities = null;
    this.form.removeEventListener('submit', this.formSubmitHandler);
  }
}

export default TicketSelector;



// WEBPACK FOOTER //
// ./V7/events/detail/TicketSelector.js